import {
  type ContactPreviewData,
  contactPreviewSchema,
} from "../../schemas/contactPreview";
import { addressBookApi } from "../../services";

// A contact that has finished loading, one way or the other
export type ContactPreviewResult =
  | { status: "loaded"; contactId: number; contact: ContactPreviewData }
  | { status: "failed"; contactId: number };

const settled = new Map<number, ContactPreviewResult>();

const inFlight = new Map<number, Promise<ContactPreviewResult>>();

export function peekContact(
  contactId: number,
): ContactPreviewResult | undefined {
  return settled.get(contactId);
}

export function loadContact(contactId: number): Promise<ContactPreviewResult> {
  const known = settled.get(contactId);

  if (known !== undefined) {
    return Promise.resolve(known);
  }

  const pending = inFlight.get(contactId);

  if (pending !== undefined) {
    return pending;
  }

  const request = fetchContact(contactId).finally(() => {
    inFlight.delete(contactId);
  });

  inFlight.set(contactId, request);

  return request;
}

async function fetchContact(contactId: number): Promise<ContactPreviewResult> {
  try {
    const payload = await addressBookApi.fetchContactsShow(
      contactId.toString(),
    );

    const result: ContactPreviewResult = {
      status: "loaded",
      contactId,
      contact: contactPreviewSchema.parse(payload),
    };

    // Only successes are kept, so asking again after a failure retries rather
    // than showing the same error for the life of the page.
    settled.set(contactId, result);

    return result;
  } catch (error) {
    console.error("Failed to load contact preview:", error);

    return { status: "failed", contactId };
  }
}
